import { useEffect, useState } from "react";
import {
  Button,
  Input,
  Label,
  ListBox,
  Select,
  TextField,
} from "@heroui/react";
import { Icon } from "@iconify/react";

import { UserRole } from "@/features/users/type.ts";
import { useCities } from "@/features/city/use-cities.ts";

export type UsersFiltersValue = {
  phone?: string;
  name?: string;
  role?: string;
  city?: number;
};

// Ключ «Все» у Select: null в selectedKey означает «ничего не выбрано»,
// а нам нужен видимый пункт, которым фильтр снимается.
const ALL = "all";

// Пользователи без города — бэкенд понимает cityId=-1 как city IS NULL.
const NO_CITY = -1;

const ROLES: { key: UserRole; title: string }[] = [
  { key: "admin", title: "Администратор" },
  { key: "courier", title: "Курьер" },
  { key: "operator", title: "Оператор" },
  { key: "user", title: "Пользователь" },
];

// Текстовые фильтры не дёргают сервер на каждый символ.
const DEBOUNCE_MS = 400;

export function UsersFilters({
  value,
  onChange,
}: {
  value: UsersFiltersValue;
  onChange: (next: UsersFiltersValue) => void;
}) {
  const cities = useCities();

  const [phone, setPhone] = useState(value.phone ?? "");
  const [name, setName] = useState(value.name ?? "");

  // Поля живут своей жизнью (дебаунс), но должны догонять адресную строку —
  // например при переходе «назад». Правка стейта прямо в рендере, а не в
  // эффекте: так React перерисовывает сразу, без лишнего кадра.
  const [urlPhone, setUrlPhone] = useState(value.phone ?? "");
  const [urlName, setUrlName] = useState(value.name ?? "");

  if (urlPhone !== (value.phone ?? "")) {
    setUrlPhone(value.phone ?? "");
    setPhone(value.phone ?? "");
  }

  if (urlName !== (value.name ?? "")) {
    setUrlName(value.name ?? "");
    setName(value.name ?? "");
  }

  useEffect(() => {
    if (phone === (value.phone ?? "")) return;

    const timer = setTimeout(
      () => onChange({ ...value, phone: phone || undefined }),
      DEBOUNCE_MS,
    );

    return () => clearTimeout(timer);
  }, [phone]);

  useEffect(() => {
    if (name === (value.name ?? "")) return;

    const timer = setTimeout(
      () => onChange({ ...value, name: name || undefined }),
      DEBOUNCE_MS,
    );

    return () => clearTimeout(timer);
  }, [name]);

  const cityItems = [
    { key: ALL, title: "Все города" },
    { key: `${NO_CITY}`, title: "Без города" },
    ...cities.data.data.map((item) => ({
      key: `${item.id}`,
      title: item.name,
    })),
  ];

  const roleItems = [
    { key: ALL, title: "Все роли" },
    ...ROLES.map(({ key, title }) => ({ key: key as string, title })),
  ];

  const isDirty = Boolean(
    value.phone || value.name || value.role || value.city,
  );

  const onReset = () => {
    setPhone("");
    setName("");
    onChange({});
  };

  return (
    <div className="flex flex-row flex-wrap gap-3 items-end">
      <TextField className="w-56" value={phone} onChange={setPhone}>
        <Label>Телефон</Label>
        <Input placeholder="+7 777 123 45 67" />
      </TextField>

      <TextField className="w-56" value={name} onChange={setName}>
        <Label>Имя</Label>
        <Input placeholder="Начните вводить имя" />
      </TextField>

      <Select
        className="w-48"
        selectedKey={value.role ?? ALL}
        onSelectionChange={(key) =>
          onChange({
            ...value,
            role: !key || key === ALL ? undefined : String(key),
          })
        }
      >
        <Label>Роль</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox items={roleItems}>
            {({ key, title }) => (
              <ListBox.Item id={key} textValue={title}>
                {title}
              </ListBox.Item>
            )}
          </ListBox>
        </Select.Popover>
      </Select>

      <Select
        className="w-48"
        selectedKey={value.city ? `${value.city}` : ALL}
        onSelectionChange={(key) =>
          onChange({
            ...value,
            city: !key || key === ALL ? undefined : Number(key),
          })
        }
      >
        <Label>Город</Label>
        <Select.Trigger>
          <Select.Value />
          <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
          <ListBox items={cityItems}>
            {({ key, title }) => (
              <ListBox.Item id={key} textValue={title}>
                {title}
              </ListBox.Item>
            )}
          </ListBox>
        </Select.Popover>
      </Select>

      {isDirty && (
        <Button variant="ghost" onPress={onReset}>
          Сбросить
          <Icon className="size-5" icon="ion:close" />
        </Button>
      )}
    </div>
  );
}
